import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import Table from "@/components/Table";
import api from "@/services/api";
import { HiPencil } from "react-icons/hi";
import TopMenu from "./components/TopMenu";
import ItemModal from "../components/itemModal";

export default ({ project }) => {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    search: "",
    nnls: [],
    module_ids: [],
    roadlog_tags: [],
    resource_profile: [],
    is_in_backlog: "",
    statuses: ["TODO", "READY_TO_DEV", "IN_PROGRESS", "NEED_INFO"],
    sprint_start_date: null,
    sprint_end_date: null,
  });
  const [sortBy, setSortBy] = useState({ key: "nnl", value: 1 });
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const itemModalId = query.get("item_modal_id");

  useEffect(() => {
    fetchItems();
  }, [filters]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data, ok } = await api.post("/roadlog_item/search", { project_id: project._id, exclude_categories: ["BUG"], ...filters });
      if (!ok) return toast.error("Failed to fetch roadlog items");
      data.sort((a, b) => (a.module_name || "").localeCompare(b.module_name || ""));
      setItems(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch roadlog items");
    } finally {
      setLoading(false);
    }
  };

  const totalNow = items.filter((e) => e.nnl === "NOW");
  const totalNext = items.filter((e) => e.nnl === "NEXT");
  const totalLater = items.filter((e) => e.nnl === "LATER");

  const handleSave = async (itemId, value) => {
    try {
      const { ok, data } = await api.put(`/roadlog_item/${itemId}`, value);
      if (!ok) return toast.error("Failed to update item");

      const index = items.findIndex((item) => item._id === itemId);
      const newItems = [...items];
      newItems[index] = { ...newItems[index], ...value };
      setItems(newItems);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update item");
    }
  };

  return (
    <div className="space-y-1">
      <TopMenu
        filters={filters}
        setFilters={setFilters}
        project={project}
        items={items}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        onMultipleDelete={() => {
          setItems(items.filter((i) => !selectedItems.some((s) => s._id === i._id)));
          setSelectedItems([]);
        }}
        onTicketCreated={(newTicket) => {
          setItems([...items, newTicket]);
        }}
      />
      <Table
        total={items.length}
        header={[
          {
            title: (
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedItems(items);
                  } else {
                    setSelectedItems([]);
                  }
                }}
              />
            ),
            position: "center",
          },
          { title: "#", key: "index" },
          { title: "Title", key: "title" },
          { title: "Priority", key: "nnl" },
          { title: "Days Required", key: "days_required" },
          { title: "Estimation", key: "estimation" },
        ]}
        loading={loading}
        height="h-[32rem]"
        sort={{ [sortBy.key]: sortBy.value }}
        onSort={(e) => setSortBy(sortBy.key === e ? { key: e, value: sortBy.value * -1 } : { key: e, value: 1 })}>
        <AnimatePresence>
          {items
            .sort((a, b) => {
              if (sortBy.key === "created_at") {
                return sortBy.value * (new Date(b.created_at) - new Date(a.created_at));
              }
              if (["days_required", "estimation", "index"].includes(sortBy.key)) {
                return sortBy.value * ((a[sortBy.key] || 0) - (b[sortBy.key] || 0));
              }
              if (sortBy.key === "nnl") {
                const order = { NOW: 1, NEXT: 2, LATER: 3 };
                return sortBy.value * ((order[a.nnl] || 99) - (order[b.nnl] || 99));
              }
              return sortBy.value * (a[sortBy.key] || "").localeCompare(b[sortBy.key] || "");
            })
            .map((item, index) => {
              const selected = selectedItems.some((e) => e._id === item._id);

              let color,
                priorityColor = "bg-white";
              let hoverColor = "hover:bg-blue-50";

              if (item.nnl === "NEXT") {
                color = "bg-blue-200";
                priorityColor = "bg-blue-300";
                hoverColor = "hover:bg-blue-300";
              }
              if (item.nnl === "LATER") {
                priorityColor = "bg-gray-300";
                color = "bg-gray-200";
                hoverColor = "hover:bg-gray-300";
              }

              return (
                <motion.tr
                  key={item._id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className={`cursor-pointer ${color} group ${hoverColor} transition-colors duration-200`}>
                  <td
                    className="border px-4 py-2 cursor-pointer w-8"
                    onClick={(e) => {
                      const find = selectedItems.some((e) => e._id === item._id);
                      if (find) {
                        setSelectedItems(selectedItems.filter((e) => e._id !== item._id));
                      } else {
                        setSelectedItems([...selectedItems, item]);
                      }
                    }}>
                    <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" checked={selected} onChange={() => {}} />
                  </td>
                  <td className="py-2 text-sm border">
                    <div className="relative flex items-center flex-1 min-w-0 text-center">
                      <span className="font-medium shrink-0 text-center">#{item.index}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-sm w-full border">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <div className="relative flex items-center gap-2 flex-1 min-w-0">
                          <div>
                            <div
                              contentEditable
                              suppressContentEditableWarning
                              onBlur={(e) => {
                                setEditingId(null);
                                handleSave(item._id, { title: e.target.textContent });
                              }}
                              onClick={() => setEditingId(item._id)}
                              className="outline-none border-b border-transparent hover:border-gray-300 focus:border-blue-500 bg-transparent px-1 line-clamp-3 overflow-hidden cursor-text">
                              {item.title}
                            </div>
                            {item.module_name && (
                              <div className="mt-1">
                                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">{item.module_name}</span>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              query.set("item_modal_id", item._id);
                              navigate({ search: query.toString() });
                            }}
                            className={`absolute top-1/2 -translate-y-1/2 right-0 px-3 py-1.5 rounded-md flex items-center gap-2 bg-white border border-gray-200 shadow-sm
                                      ${editingId === item._id ? "hidden" : "invisible group-hover:visible"}`}>
                            <HiPencil className="w-4 h-4 text-gray-600" />
                            <span className="text-xs font-medium text-gray-600">Open</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>

                  <td onClick={(e) => e.preventDefault()} className="border px-4 py-2 max-w-xs truncate">
                    <select
                      value={item.nnl}
                      onChange={async (e) => {
                        const { ok, data } = await api.put(`/roadlog_item/${item._id}`, { nnl: e.target.value });
                        if (!ok) return toast.error("Failed to update NNL");
                        console.log(data);
                        setItems(items.map((i) => (i._id === item._id ? data : i)));
                        toast.success("NNL updated!");
                      }}
                      className={`w-28 px-2 py-1 rounded text-xs ${priorityColor} cursor-pointer appearance-none hover:opacity-80`}>
                      <option value="NOW">NOW</option>
                      <option value="NEXT">NEXT</option>
                      <option value="LATER">LATER</option>
                    </select>
                  </td>

                  <td className="px-4 py-2 border">
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => {
                        const days = Number(e.target.textContent) || 0;
                        handleSave(item._id, { days_required: days, estimation: days * (item.tjm || 0) });
                      }}
                      className="outline-none border-b border-transparent hover:border-gray-300 focus:border-blue-500 bg-transparent px-1 line-clamp-3 overflow-hidden cursor-text">
                      {item.days_required}
                    </div>
                  </td>
                  <td className="border px-4 py-2 max-w-xs truncate">{item.estimation ? `${formatNumberThreeDigits(item.estimation)} €` : ""}</td>
                </motion.tr>
              );
            })}
        </AnimatePresence>
      </Table>

      <table className="w-full border-collapse border border-gray-200">
        <tbody>
          <TotalTable totalNow={totalNow} totalNext={totalNext} totalLater={totalLater} items={items} />
        </tbody>
      </table>
      {itemModalId && (
        <ItemModal
          project={project}
          onClose={() => {
            query.delete("item_modal_id");
            navigate({ search: query.toString() });
          }}
          onUpdate={(item) => setItems(items.map((i) => (i._id === item._id ? item : i)))}
          onDelete={(item) => setItems(items.filter((i) => i._id !== item._id))}
        />
      )}
    </div>
  );
};

const TotalTable = ({ totalNow, totalNext, totalLater, items }) => {
  return (
    <>
      <tr className="">
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{totalNow.length} items</td>
        <td className="border px-4 py-2 whitespace-normal text-sm">
          Short-term features that are priority and MUST for an MVP. These are the key features of a product in addition to the basics (admin + deployment + metabase). The features
          that will be developed in the MVP.{" "}
        </td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{totalNow.reduce((acc, item) => acc + (item.days_required || 0), 0).toFixed(2)}</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{formatNumberThreeDigits(totalNow.reduce((acc, item) => acc + Number(item.estimation || 0), 0))} €</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">NOW</td>
      </tr>
      <tr className="bg-blue-200">
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{totalNext.length} items</td>
        <td className="border px-4 py-2 whitespace-normal text-sm">The medium-term functionalities which enrich the product, without these the product already works. </td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{totalNext.reduce((acc, item) => acc + (item.days_required || 0), 0).toFixed(2)}</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{formatNumberThreeDigits(totalNext.reduce((acc, item) => acc + Number(item.estimation || 0), 0))} €</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">NEXT</td>
      </tr>
      <tr className="bg-gray-200">
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{totalLater.length} items</td>
        <td className="border px-4 py-2 whitespace-normal text-sm">Long-term functionalities are functionalities which are not defined and difficult to estimate.</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{totalLater.reduce((acc, item) => acc + (item.days_required || 0), 0)}</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{formatNumberThreeDigits(totalLater.reduce((acc, item) => acc + Number(item.estimation || 0), 0))} €</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">LATER</td>
      </tr>
      <tr className="bg-sky-700 text-white font-bold">
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{items.length} items</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm"></td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{items.reduce((acc, item) => acc + (item.days_required || 0), 0).toFixed(2)}</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{formatNumberThreeDigits(items.reduce((acc, item) => acc + Number(item.estimation || 0), 0))} €</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">Total</td>
      </tr>
    </>
  );
};

const formatNumberThreeDigits = (number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};
